// @flow

import React, { PureComponent } from 'react';
import AvatarHoverCardBackgroundPlaceholder from 'app/common/react/components/AvatarHoverCard/AvatarHoverCardBackgroundPlaceholder';
import i18n from 'app/common/lib/i18n';
import type { GridPin } from 'app/packages/pinterest-flow-types/pin';
import type { ShowcaseBoardType } from 'app/common/react/components/profile/showcase/commonTypes';
import { Box, Image } from 'gestalt';

type Props = {|
  isHorizontalDesign: boolean,
  showcase: ?Array<ShowcaseBoardType>,
  userPins: ?Array<GridPin>,
|};

type State = {|
  isLoaded: Array<boolean>,
  isMounted: boolean,
|};

const BACKGROUND_HEIGHT_PIXELS = 136;
const gradientStyle = {
  __style: { background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.43) 0%, rgba(0,0,0,0) 60%)' },
};

export default class AvatarHoverCardBackground extends PureComponent<Props, State> {
  state = {
    // keep track of if images have loaded to perform animation on load
    isLoaded: [false, false, false],
    isMounted: false,
  };

  componentDidMount() {
    this.setState({
      isMounted: true,
    });
    this.getImageArray();
  }

  getImageArray = () => {
    const { userPins, showcase } = this.props;
    let images: Array<string> = [];

    if (!userPins || userPins.length === 0) {
      return images;
    } else if (!showcase || showcase.length === 0) {
      images = userPins.map(
        (pin) => (pin.images['136x136'] || pin.images['236x'] || pin.images['736x']).url,
      );
    } else if (!!showcase && showcase.length === 1) {
      images = showcase
        .filter((showcaseItem) => showcaseItem.type === 'board')[0]
        .images['550x'].map((image) => image.url);
    } else if (!!showcase) {
      images = showcase
        .filter((showcaseItem) => showcaseItem.type === 'board')
        .map((showcaseItem) => showcaseItem.image_cover_url);
    }

    return images;
  };

  getImage = (imageUrl: string, index: number) => {
    const { isLoaded } = this.state;

    const wrapperStyle = {
      transition: 'opacity 500ms',
      opacity: isLoaded[index] ? 1 : 0,
    };

    return (
      <Box
        dangerouslySetInlineStyle={{ __style: wrapperStyle }}
        color="white"
        height="100%"
        width="100%"
      >
        {!!imageUrl && (
          <Image
            naturalHeight={1}
            src={imageUrl}
            fit="cover"
            alt={i18n._("user's pin", "image of one of a user's pins")}
            naturalWidth={1}
            color="lightGray"
            onLoad={() => this.handleLoad(index)}
          />
        )}
      </Box>
    );
  };

  handleLoad = (index: number) => {
    const { isLoaded, isMounted } = this.state;
    const newIsLoaded = [...isLoaded];
    newIsLoaded[index] = true;

    if (isMounted) {
      this.setState({
        isLoaded: newIsLoaded,
      });
    }
  };

  render() {
    const { isHorizontalDesign } = this.props;
    const images = this.getImageArray();

    // user's images to be loaded
    let contents = null;

    if (images.length === 1) {
      contents = (
        <Box height="100%" display="flex" direction="row">
          {this.getImage(images[0], 0)}
        </Box>
      );
    } else if (images.length === 2) {
      contents = (
        <Box height="100%" display="flex" direction="row">
          {this.getImage(images[0], 0)}
          <Box color="white" width="1px" flex="none" />
          {this.getImage(images[1], 1)}
        </Box>
      );
    } else if (isHorizontalDesign) {
      contents = (
        <Box height="100%" width="100%" display="flex" direction="row">
          <Box height="100%" width="100%">
            {this.getImage(images[0], 0)}
          </Box>
          <Box color="white" width="2px" flex="none" />
          <Box height="100%" width="100%">
            {this.getImage(images[1], 1)}
          </Box>
          <Box color="white" width="2px" flex="none" />
          <Box height="100%" width="100%">
            {this.getImage(images[2], 2)}
          </Box>
        </Box>
      );
    } else {
      contents = (
        <Box height="100%" width="100%" display="flex" direction="row">
          <Box height="100%" flex="grow">
            {this.getImage(images[0], 0)}
          </Box>
          <Box color="white" width="2px" flex="none" />
          <Box display="flex" height={BACKGROUND_HEIGHT_PIXELS} direction="column" flex="grow">
            <Box width="100%" flex="grow">
              {this.getImage(images[1], 1)}
            </Box>
            <Box color="white" height="2px" flex="none" />
            <Box width="100%" flex="grow">
              {this.getImage(images[2], 2)}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box height={BACKGROUND_HEIGHT_PIXELS} display="flex" direction="column">
        <Box width="100%" position="relative" flex="grow">
          <AvatarHoverCardBackgroundPlaceholder isHorizontalDesign={isHorizontalDesign} />
          {contents}
          <Box
            dangerouslySetInlineStyle={gradientStyle}
            height="100%"
            width="100%"
            position="absolute"
            top
            left
          />
        </Box>
      </Box>
    );
  }
}
